/**
 * Profile Questionnaire — wire-normalization helpers.
 *
 * The B-2.A contract (see
 * docs/epics/profile-questionnaire-detail-slice-b-2/b2-a-contract-transition01.md,
 * published at
 * docs/epics/profile-questionnaire-detail-slice-b-2/backend-dependency.md
 * §2) carries three optional string fields
 * (work_sphere / education_sphere / education_type) on the wire.
 *
 * Per ACT-INDEEP-B2-A-FRONTEND-CONTRACT-ALIGNMENT01 (Option A, the
 * backend's superseding B-2.A contract), the wire shape is:
 *
 *   - undefined             → canonical unset (no violation)
 *   - null                  → canonical unset (no violation)
 *   - '' (empty string)     → canonical unset (no violation)
 *   - any non-empty string  → preserve verbatim (NO violation,
 *                              regardless of corpus membership)
 *   - non-string (number,
 *     boolean, object,
 *     array)                → canonical unset + explicit protocol
 *                              violation surfaced to the reducer
 *
 * Crucially, this layer does NOT consult any local UX corpus
 * (WORK_EDUCATION_SPHERES, EDUCATION_TYPES) for wire validation.
 * Those lists are UX-suggestion corpus only, used by the picker
 * controls in view.tsx; they have no authority on whether a
 * server value is valid.
 *
 * Strictness is preserved but its authority shifted: from "corpus
 * membership" to "wire-shape type". The R1/R2 invariant still
 * holds — junk wire types MUST surface as a visible serverError,
 * not silently collapse to "no value".
 *
 * "None selected" or "Не указано" is not a magic string — it is
 * the undefined form-state value. The view's SelectControl uses
 * value === '' to render the empty option; this module
 * canonicalises the wire shape to undefined.
 */

import type { ProfileForm } from './model';

/**
 * Result of a strict normalization. value is the canonical form-
 * state (undefined when unset, string when valid). When the
 * server returned a wire-shape the FE cannot trust (only
 * non-string types in the Option-A contract), protocolViolations
 * carries a human-readable reason so the caller can surface it.
 *
 * The R1 fix (corpus-membership strictness) is intentionally
 * retired in this ACT. The R2 lifecycle strictness in
 * lifecycle.ts is unchanged.
 */
export interface NormalizedField<T extends string> {
  readonly value: T | undefined;
  readonly protocolViolations: readonly string[];
}

/**
 * Field labels for the three optional B-2.A fields. Used by the
 * one-size-fits-all helper to produce informative violation
 * messages.
 */
export const WORK_SPHERE_FIELD = 'work_sphere' as const;
export const EDUCATION_SPHERE_FIELD = 'education_sphere' as const;
export const EDUCATION_TYPE_FIELD = 'education_type' as const;

/**
 * The canonical wire-rules for all three optional B-2.A fields.
 *
 * Per ACT-INDEEP-B2-A-FRONTEND-CONTRACT-ALIGNMENT01 Option A:
 *   - canonical unset (undefined / null / '') → { undefined, [] }
 *   - any non-empty string                    → { value, [] }
 *   - wrong wire type                         → { undefined, [violation] }
 *
 * This is the single source of truth for the three normalize*
 * helpers. They are thin wrappers that fix the field name.
 */
export function normalizeOptionalProfileString(
  value: unknown,
  fieldName: string
): NormalizedField<string> {
  if (value === undefined || value === null || value === '') {
    return { value: undefined, protocolViolations: [] };
  }
  if (typeof value === 'string') {
    return { value, protocolViolations: [] };
  }
  return {
    value: undefined,
    protocolViolations: [
      `${fieldName} has wrong wire type: ${JSON.stringify(value)}`,
    ],
  };
}

/**
 * Convenience helper: normalize a work_sphere field.
 */
export function normalizeWorkSphere(value: unknown): NormalizedField<string> {
  return normalizeOptionalProfileString(value, WORK_SPHERE_FIELD);
}

/**
 * Convenience helper: normalize an education_sphere field. Same
 * shape as normalizeWorkSphere.
 */
export function normalizeEducationSphere(value: unknown): NormalizedField<string> {
  return normalizeOptionalProfileString(value, EDUCATION_SPHERE_FIELD);
}

/**
 * Convenience helper: normalize an education_type field. Same
 * shape as normalizeWorkSphere.
 */
export function normalizeEducationType(value: unknown): NormalizedField<string> {
  return normalizeOptionalProfileString(value, EDUCATION_TYPE_FIELD);
}

/**
 * Convenience helper: apply the three optional-field normalizers
 * to a ProfileForm and return the merged result. Used by callers
 * that already have a ProfileForm and want to canonicalise. Kept
 * here so the "preserve any string" rule lives in exactly one
 * place.
 *
 * The returned form is a NEW object; the input is not mutated.
 * The three optional-string fields are taken from the normalize
 * result; every other field is preserved unchanged.
 */
export function normalizeOptionalProfileStrings(
  form: ProfileForm
): {
  readonly form: ProfileForm;
  readonly protocolViolations: readonly string[];
} {
  const violations: string[] = [];

  const work = normalizeOptionalProfileString(form.work_sphere, WORK_SPHERE_FIELD);
  violations.push(...work.protocolViolations);

  const edu = normalizeOptionalProfileString(
    form.education_sphere,
    EDUCATION_SPHERE_FIELD
  );
  violations.push(...edu.protocolViolations);

  const type = normalizeOptionalProfileString(
    form.education_type,
    EDUCATION_TYPE_FIELD
  );
  violations.push(...type.protocolViolations);

  const rebuilt: ProfileForm = {
    display_name: form.display_name,
    gender: form.gender,
    scenario: form.scenario,
    age: form.age,
    city: form.city,
    headline: form.headline,
    bio: form.bio,
    ...(work.value !== undefined ? { work_sphere: work.value } : {}),
    ...(edu.value !== undefined ? { education_sphere: edu.value } : {}),
    ...(type.value !== undefined ? { education_type: type.value } : {}),
  };

  return { form: rebuilt, protocolViolations: violations };
}
